import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const recommendations = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        const sharedGenres = m.genres.filter((g) => movie.genres.includes(g)).length;
        const sameDirector = m.director === movie.director ? 1 : 0;
        const sameType = m.type === movie.type ? 1 : 0;
        return { m, score: sharedGenres * 2 + sameDirector + sameType };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((item) => item.m);
  }, [movie]);

  usePageMeta({
    title: movie ? `${movie.title} - 伊人在线` : '影视内容不存在 - 伊人在线',
    description: movie
      ? `${movie.title}（${movie.year}·${movie.region}）${movie.genres.join('、')}，导演${movie.director}，主演${movie.actors.join('、')}。${movie.description.slice(0, 60)}`
      : '影视内容不存在或已下架',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},伊人在线` : '伊人在线',
  });

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex min-h-[70vh] items-center justify-center px-4 pt-16">
          <div className="max-w-md text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
              影视内容不存在或已下架
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              你访问的影视可能已经移除了，返回首页看看其他精彩内容吧。
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link
                to="/"
                className="whitespace-nowrap cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to="/category"
                className="whitespace-nowrap cursor-pointer rounded-md bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200"
              >
                浏览分类
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(movie.id);
    setToast(nowFavorited ? '已加入收藏（本地演示）' : '已取消收藏');
  };

  const infoRows = [
    { label: '类型', value: movie.type, icon: 'ri-movie-2-line' },
    { label: '地区', value: movie.region, icon: 'ri-map-pin-line' },
    { label: '年份', value: String(movie.year), icon: 'ri-calendar-line' },
    { label: '时长', value: movie.duration, icon: 'ri-time-line' },
    { label: '导演', value: movie.director, icon: 'ri-clapperboard-line' },
    { label: '主演', value: movie.actors.join('、'), icon: 'ri-user-star-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <div className="relative overflow-hidden bg-primary-950">
          <img
            src={movie.backdrop || movie.poster}
            alt={`${movie.title} 背景图`}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/40"></div>

          <div className="relative mx-auto px-4 py-8 md:px-10 md:py-12">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-background-300" aria-label="面包屑">
              <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={`/category?type=${encodeURIComponent(movie.type)}`}
                className="cursor-pointer transition-colors hover:text-accent-400"
              >
                {movie.type}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">{movie.title}</span>
            </nav>

            <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
              <div className="w-[160px] flex-shrink-0 self-center overflow-hidden rounded-lg ring-1 ring-background-50/20 md:w-[220px] md:self-start">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 伊人在线影视`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
                  {movie.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                  <span className="flex items-center gap-1 text-accent-400">
                    <i className="ri-star-fill"></i>
                    <span className="font-semibold">{movie.rating.toFixed(1)}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-fill text-accent-400"></i>
                    {(movie.heat / 10000).toFixed(1)} 万热度
                  </span>
                  {movie.episodes && (
                    <span className="flex items-center gap-1">
                      <i className="ri-list-check"></i>
                      全 {movie.episodes} 集
                    </span>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {movie.genres.map((g) => (
                    <span
                      key={g}
                      className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50"
                    >
                      {g}
                    </span>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex items-start gap-2 text-sm">
                      <i className={`${row.icon} mt-0.5 text-background-300`}></i>
                      <span className="text-background-300">{row.label}：</span>
                      <span className="truncate text-background-50">{row.value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                      favorited
                        ? 'bg-accent-500 text-accent-950 hover:bg-accent-600'
                        : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
                    }`}
                  >
                    <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                    {favorited ? '已收藏' : '收藏'}
                  </button>
                  <Link
                    to={`/category?type=${encodeURIComponent(movie.type)}`}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
                  >
                    <i className="ri-links-line"></i>
                    返回{movie.type}列表
                  </Link>
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-background-400">
                  <i className="ri-information-line"></i>
                  本站为影视信息展示平台，播放与下载功能为演示说明，暂未提供。
                </p>
              </div>
            </div>
          </div>
        </div>

        <section className="mx-auto px-4 py-10 md:px-10 md:py-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-book-open-line text-primary-600"></i>
            剧情简介
          </h2>
          <p className="mt-4 max-w-4xl text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
            {movie.description}
          </p>
        </section>

        <section className="bg-background-100/60">
          <div className="mx-auto px-4 py-10 md:px-10 md:py-14">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-magic-line text-accent-600"></i>
              相关推荐
            </h2>
            <p className="mt-1 text-sm text-foreground-600">根据类型与导演为你精选的相似作品</p>

            {recommendations.length === 0 ? (
              <div className="mt-6 rounded-lg bg-background-50 py-14 text-center ring-1 ring-background-200/70">
                <p className="text-sm text-foreground-600">暂无相关影视资源</p>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5">
                {recommendations.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="mx-auto flex flex-wrap items-center gap-3 px-4 py-8 md:px-10">
          <Link
            to="/"
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
          <Link
            to="/category"
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-grid-line"></i>
            返回分类
          </Link>
        </div>
      </main>
      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-fade-up rounded-md bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}